/**
 * @file A paddle and ball game controller.
 * @author Gonzalo Alvarez de Aldecoa <gon@galdecoa.com>
 * @license GPL-3.0-or-later
 * @version 2307.01.001
 */

/** @module paddleandballgame */

import { Box } from "./Box.js";

/**
 * A representation of a rectangle with a colour.
 * @class
 * @augments module:box~Box
 */
class Block extends Box {
	/** 
	 * The block's colour.
	 * @type {String} 
	 * @default #027cb0
	 */
	colour = "#027cb0";
	/**
	 * Create a Block.
	 * Origin of coordinates is at the top left, with x increasing to the right
	 * and y increasing downwards.
	 * @param {Number} [x=0]      
	 * @param {Number} [y=0]
	 * @param {Number} [width=0]        Cannot be a negative number.
	 * @param {Number} [height=0]       Cannot be a negative number.
	 * @param {String} [colour=#027cb0] A colour value.
	 */
	constructor( x = 0, y = 0, width = 0, height = 0, colour = "#027cb0" ) {
		super( x, y, width, height );
		this.colour = colour;
	}
}

/**
 * A controller for paddle and ball game.
 * @class
 */
class PaddleAndBallGame {

	/**
	* Generate a sequence of colours in which the chromatic progression is 
	* continuous even if looped.
	* It is achieved using three 120° out-of-phase sine waves to produce colours 
	* such that their hue travels around the circle of a colour wheel.
	* @see {@link https://krazydad.com/tutorials/makecolors.php "Tutorial: Making annoying rainbows and other color cycles in Javascript" by Jim Bumgardner}
	* @memberof module:paddleandballgame~PaddleAndBallGame
  * @access private
	* @function generateColourCycle
	* @param   {number}		numberOfColours 
	* @returns {string[]}
	*/
  static #generateColourCycle( numberOfColours ) {
		const twoPi = 2 * Math.PI;
		const colours = new Array( numberOfColours ); 
		const theta = twoPi / numberOfColours ;
		const phase1 = 0;					   //   0°
		const phase2 = twoPi / 3;	   // 120°
		const phase3 = 2 * phase2;   // 240°
		for ( let c = numberOfColours - 1; c >= 0; c = c - 1 ) {
				const frequency = theta * c;
				const r = Math.round( Math.sin( frequency + phase1 ) * 127.5 + 127 );
				const g = Math.round( Math.sin( frequency + phase2 ) * 127.5 + 127 );
				const b = Math.round( Math.sin( frequency + phase3 ) * 127.5 + 127 );
				// Convert to integer, using the corresponding powers of 16, then to hex
				const hexColour = ( r * 65536 + g * 256 + b ).toString( 16 );
				// Add zero padding and number sign prefix
				colours[ c ] = "#" + ( "00000" + hexColour ).slice( -6 );
		}
		return colours;
	}

	/** 
	 * The `canvas` element to render the game in.
	 * @type {HTMLCanvasElement?}
	 */
	#canvas = null;
	/** 
	 * The [`canvas`]{@link module:paddleandballgame~PaddleAndBallGame#canvas}' 2d context.
	 * @type {CanvasRenderingContext2D?}
	 */
	#context = null;

	/** 
	 * The Box describing the playing field. 
	 * @type {(Block)?} 
	 */
	#field = null;

	/** 
	 * The paddle for the game.
	 * @type {(Block)?} 
	 */
	#paddle = null;
	/**
	 * Rebound angle ranges, in radians, for ball vs. paddle collision.
	 * Taking into account `y` values point downwards in canvas, if the ball is 
	 * bouncing off the left-hand side of the paddle, if will rebound with an 
	 * angle between 200° and 250°. If it's the right-hand side, the angle range 
	 * is 290° to 340°. 
	 * @type {Object}
	 */
	#paddleReboundAngleRange = {
		left:  { min: 200 * Math.PI / 180, max: 250 * Math.PI / 180 },
		right: { min: 290 * Math.PI / 180, max: 340 * Math.PI / 180 }
	};

	/** 
	 * The ball for the game.
	 * @type {(Block)?} 
	 */
	#ball = null;
	/** 
	 * The ball's velocity vector's modulus.
	 * @type {Number} 
	 * @default 4
	 */
	#ballVelocityMagnitude = 4;
	/** 
	 * The ball's velocity vector. Depends on [`ballVelocityMagnitude`]{@link module:paddleandballgame~PaddleAndBallGame#ballVelocityMagnitude}.
	 * @type {Object} 
	 * @see {@link module:paddleandballgame~PaddleAndBallGame#ballVelocityMagnitude ballVelocityMagnitude}
	 * @default { x: 2.8284, y: -2.8284 }
	 */
	#ballVelocity = {
		x:  this.#ballVelocityMagnitude * Math.cos( Math.PI / 4 ),
		y: -this.#ballVelocityMagnitude * Math.sin( Math.PI / 4 ),
	};

	/** 
	 * The collection of bricks to be destroyed.
	 * @type {Block[]} 
	 */
	#bricks = [];
	/** 
	 * The number bricks at the start of the game.
	 * @type {Number} 
	 */
	#numberOfBricks = 0;

	/** 
	 * Minimum duration of a frame. Inversely proportional to the maximum 
	 * framerate. For example, a 60 FPS limit implies a minFrameDuration of 
	 * 1000 / 60 ≈ 16.667
	 * @type {Number} 
	 * @default 16.667
	 */
	#minFrameDuration = 1000 / 60; // Inverse of 60 frames per thousand ms
	/** 
	 * The last timestamp a frame was processed at. 
	 * @type {Number} 
	 */
	#previousFrameTimestamp = 0;

	/**
	 * The requestAnimationFrame identifier.
	 * @type {Number}
	 */
	#requestAnimationFrameId = 0;
	
	/** 
	 * Flags when the game is paused, i.e., when ball and paddle do not move.
	 * Plus, an informational text overlay will be shown, with the game's stats
	 * and instructions to continue playing.
	 * @type {Boolean} 
	 */
	#isPaused = true;
	/** 
	 * Flags when the game is over, regardless of the cause.
	 * @type {Boolean} 
	 */
	#isGameOver = false;

	/**
	 * The `window`'s `mouseup` event handler.
	 * @memberof module:paddleandballgame~PaddleAndBallGame#
	 * @access private
	 * @function handleWindowMouseUp
	 * @param {MouseEvent} mouseUpEvent 
	 */
	#handleWindowMouseUp = function( mouseUpEvent ) {  };
	/**
	 * The `window`'s `mousemove` event handler.
	 * @memberof module:paddleandballgame~PaddleAndBallGame#
	 * @access private
	 * @function handleWindowMouseMove
	 * @param {MouseEvent} mouseMoveEvent 
	 */
	#handleWindowMouseMove = function( mouseMoveEvent ) {  };

	/** @type {Object} */
	#keyBindings = { LEFT: "a", RIGHT: "d", START: "s" };
	/** @type {Set} */
	#pressedKeys = new Set();
	/** @type {String} */
	#activeKey = "";
	/**
	 * The `window`'s `keydown`/`keyup` event handler.
	 * @memberof module:paddleandballgame~PaddleAndBallGame#
	 * @access private
	 * @function handleWindowKeyboardEvent
	 * @param {MouseEvent} keyboardEvent 
	 */
	#handleWindowKeyboardEvent = function( keyboardEvent ) {};

	/** @type {Boolean} */
	#isUsingTouch = false;

	/**
	 * The `window`'s `touchstart`/`touchend` event handler.
	 * @memberof module:paddleandballgame~PaddleAndBallGame#
	 * @access private
	 * @function handleTouchEnd
	 * @param {TouchEvent} touchEvent 
	 */
	#handleWindowTouchEvent = function( touchEvent ) {  };

	/**
	 * The `document`'s `visibilitychange` event handler.
	 * @memberof module:paddleandballgame~PaddleAndBallGame#
	 * @access private
	 * @function handleDocumentVisibilityChange
	 * @param {Event} visibilityChangeEvent 
	 */
	#handleDocumentVisibilityChange = function( visibilityChangeEvent ) { };

	/**
	 * The `window`'s `blur` event handler.
	 * @memberof module:paddleandballgame~PaddleAndBallGame#
	 * @access private
	 * @function handleWindowBlur
	 * @param {Event} blurEvent 
	 */
	#handleWindowBlur = function( blurEvent ) {  };

	/** 
	 * Set up the `canvas` element.
	 * @memberof module:paddleandballgame~PaddleAndBallGame#
	 * @access private
	 * @function setUpCanvasAndContext
	 * @param {HTMLCanvasElement} canvas
	 */
	#setUpCanvasAndContext( canvas ) {

		const boundingClientRect = canvas.getBoundingClientRect();
		canvas.width = boundingClientRect.width;
		canvas.height = boundingClientRect.height;

		this.#canvas = canvas;

		const contextAttributes = { alpha: false, desynchronized: false };
		this.#context = this.#canvas.getContext( "2d", contextAttributes );

	}

	/** 
	 * Create the game's paddle. 
	 * @memberof module:paddleandballgame~PaddleAndBallGame#
	 * @access private
	 * @function createPaddle
	 * @param {String} [colour=#f90]
	 */
	#createPaddle( colour = "#f90" ) {
		const width = this.#canvas.width / 10; // In proportion to the canvas' width
		const height = width * 9 / 32;         // Use an elongated aspect ratio
		this.#paddle = new Block( 
			( this.#canvas.width - width ) / 2, // Centered horizontally
			this.#canvas.height - height * 3,   // Leaving some space from the bottom
			width, 
			height,
			colour                              
		);
	}

	/** 
	 * Create the game's ball. 
	 * Requires [`createPaddle`](PaddleAndBallGame#createPaddle) to have been run first.
	 * @memberof module:paddleandballgame~PaddleAndBallGame#
	 * @access private
	 * @function createBall
	 * @param {String} [colour=#fff]
	 */
	#createBall( colour = "#fff" ) {
		const radius = Math.min( this.#canvas.width, this.#canvas.height ) / 60;
		const length = Math.sqrt( Math.PI * Math.pow( radius, 2 ) );
		this.#ball = new Block( 
			this.#paddle.x + ( this.#paddle.width - length ) / 2, // Center over paddle
			this.#paddle.y - 1 - length, // 1px separation to avoid collision
			length, 
			length,
			colour
		);
		const piFourth = Math.PI / 4;
		this.#ballVelocityMagnitude = this.#canvas.height / 60;
		this.#ballVelocity = {
			x:  this.#ballVelocityMagnitude * Math.cos( piFourth ),
			y: -this.#ballVelocityMagnitude * Math.sin( piFourth ),
		};
	}

	/**
	 * Create the blocks' boxes.
	 * @memberof module:paddleandballgame~PaddleAndBallGame#
   * @access private
	 * @function createBricks
	 * @param {Number} [numberOfColumns=15]
	 * @param {Number} [numberOfRows=6]
	 */
	#createBricks( numberOfColumns = 15, numberOfRows = 6 ) {

		this.#bricks = [];

		const bricksWidth = this.#canvas.width * 0.75;
		const bricksXOffset = ( this.#canvas.width - bricksWidth ) / 2;
		const bricksYOffset = this.#canvas.height * 0.05;

		const brickWidth = bricksWidth / numberOfColumns;
		const brickHeight = brickWidth * 9 / 16;

		const numberOfColours = Math.floor( 1.25 * numberOfRows );
		const colours = PaddleAndBallGame.#generateColourCycle( numberOfColours );

		for( let r = numberOfRows - 1; r >= 0; r = r - 1 ) {
			for ( let c  = numberOfColumns -1; c >= 0; c = c - 1 ) {
				this.#bricks.push( new Block(
					bricksXOffset + c * brickWidth, 
					bricksYOffset + r * brickHeight, 
					brickWidth, 
					brickHeight,
					colours[ r ]
				) );
			}
		}

		this.#numberOfBricks = this.#bricks.length;

	}

	/** 
	 * Initialise all the game elements.
	 * @memberof module:paddleandballgame~PaddleAndBallGame#
   * @access private
	 * @function createNewGame
	 */
	#createElements() {
		this.#field = new Block( 
			0, 0, this.#canvas.width, this.#canvas.height, "#000"
		);
		this.#createPaddle(); // Default colour: #f90
		this.#createBall();   // Default colour: #fff
		this.#createBricks(); // Default rows×columns: 15×6
	}

	/** 
	 * Check if the ball is colliding with a wall and react accordingly.
	 * @memberof module:paddleandballgame~PaddleAndBallGame#
	 * @access private
	 * @function processBallVsWallCollision
	 * @return {Boolean} `true` if the ball has hit the bottom of the field.
	 */
	#processBallVsWallCollision() {
		// Early exit if the ball has not exceeded the field limits.
		if ( this.#ball.isInside( this.#field ) ) { return false; }
		// Check for GAME OVER condition
		if ( this.#ball.maxY >= this.#field.maxY ) { return true; }
		// Check for collision against the left or right boundaries
		if (
				( this.#ball.minX <= this.#field.minX )
			|| ( this.#ball.maxX >= this.#field.maxX )
		) {
			this.#ballVelocity.x *= -1;
			this.#ball.x += this.#ballVelocity.x; // Put ball back inside field
		}
		// Check for collision against the top boundary
		if ( this.#ball.minY <= this.#field.minY ) {
			this.#ballVelocity.y *= -1;
			this.#ball.y += this.#ballVelocity.y; // Put ball back inside field
		}
	}

	/** 
	 * Check if the ball is colliding with a brick and react accordingly.
	 * If the ball is colliding with multiple bricks, it only considers the brick
	 * that has a bigger intersection area.
	 * @memberof module:paddleandballgame~PaddleAndBallGame#
	 * @access private
	 * @function processBallVsBricksCollision
	 * @return {Boolean} `true` if the all bricks have been destroyed.
	 */
	#processBallVsBricksCollision() {
		const numberOfBricks = this.#bricks.length;
		if ( numberOfBricks === 0 ) { return true; } // Signal Game Over
		// Determine the brick that has the biggest intersection area with the ball.
		let collidedBrickIdx = -1;
		let collisionArea = 0;
		for( let b = numberOfBricks - 1; b >= 0; b = b - 1 ) {
			const intersection = this.#bricks[ b ].intersect( this.#ball );
			if ( !intersection ) { continue; }
			if ( intersection.area >= collisionArea ) {
				collidedBrickIdx = b;
				collisionArea = intersection.area;
			}
		}
		// If none is found, the ball has not hit any bricks.
		if ( collidedBrickIdx == -1 ) { return false; }
		// Revert the ball's position.
		this.#ball.x -= this.#ballVelocity.x;
		this.#ball.y -= this.#ballVelocity.y;
		// Determine the rebound for the ball's position relative to the brick.
		const brick = this.#bricks[ collidedBrickIdx ];
		if ( this.#ball.isLeft( brick ) || this.#ball.isRight( brick ) ) {
			this.#ballVelocity.x *= -1;
		}
		if ( this.#ball.isAbove( brick ) || this.#ball.isBelow( brick ) ) {
			this.#ballVelocity.y *= -1;
		}
		// Remove brick from collection.
		this.#bricks.splice( collidedBrickIdx, 1 );
		// Signal Game Over (when one brick was left and has been destroyed)
		return ( numberOfBricks === 1 );
	}

	/** 
	 * Check if the paddle has hit the ball (or viceversa) and react accordingly.
	 * The paddle will change the ball's movement direction in a degree relative
	 * to the impact position.
	 * @memberof module:paddleandballgame~PaddleAndBallGame#
	 * @access private
	 * @function processBallVsPaddleCollision
	 * @return {Boolean} `true` if paddle and ball have collided
	 */
	#processBallVsPaddleCollision() {

		const intersection = this.#ball.intersect( this.#paddle );

		// Early exit if the ball has not hit the paddle (or viceversa).
		if ( !intersection ) { return false; }

		// Push the ball above the paddle
		this.#ball.y -= intersection.height; 

		// Determine reference x coordinates
		const halfBallWidth = this.#ball.width / 2;
		const ballMiddleX = ( this.#ball.x + halfBallWidth );
		const halfPaddleWidth = this.#paddle.width / 2
		const paddleMiddleX = ( this.#paddle.x + halfPaddleWidth );
		// Determine in which side of the paddle is the point of impact
		const hasHitLeft = ballMiddleX < paddleMiddleX;

		// Establish the extrema of the ball's position relative to the segment
		const segmentMinX = hasHitLeft 
			? ( this.#paddle.x - halfBallWidth )
			: paddleMiddleX
		;
		const segmentMaxX = hasHitLeft 
			? paddleMiddleX
			: ( this.#paddle.maxX + halfBallWidth )
		;

		let perunit = ( ballMiddleX - segmentMinX ) / ( segmentMaxX - segmentMinX );
		if ( perunit < 0 ) { perunit = 0; }
		if ( perunit > 1 ) { perunit = 1; }
		
		const angleRange = this.#paddleReboundAngleRange[ 
			hasHitLeft ? "left" : "right" 
		];

		// LERP
		const angle = angleRange.min 
			+ perunit * ( angleRange.max - angleRange.min )
		;

		this.#ballVelocity.x = this.#ballVelocityMagnitude * Math.cos( angle );
		this.#ballVelocity.y = this.#ballVelocityMagnitude * Math.sin( angle );

		return true;
		
	}

	/** Puts all elements in their initial state and unpauses de game */
	#restart() {
		// Clear pressed keys tracking
		this.#activeKey = null;
		this.#pressedKeys = new Set();
		// Re-position all the game elements by re-creating them. Not very elegant.
		this.#createElements(); 
		// Restart the game.
		this.#isGameOver = false;
		this.#isPaused = false;
	}

	/**
	 * Start/pause the game. 
	 * @memberof module:paddleandballgame~PaddleAndBallGame#
	 * @access private
	 * @function onWindowMouseUp
	 * @param {MouseEvent} mouseUpEvent 
	 */
	#onWindowMouseUp( mouseUpEvent ) {
		if ( this.#isUsingTouch ) { return; }
		if ( this.#isGameOver ) { return this.#restart(); }
		this.#isPaused = !this.#isPaused; // Pause/unpause
	}

	/**
	 * Move the paddle following the pointer. 
	 * @memberof module:paddleandballgame~PaddleAndBallGame#
   * @access private
	 * @function onWindowMouseMove
	 * @param {MouseEvent} mouseMoveEvent 
	 */
	#onWindowMouseMove( mouseMoveEvent ) {
		if (
			   this.#isUsingTouch
			|| this.#isPaused
			|| ( 
				   this.#ball.isBelow( this.#paddle ) 
				&& ( 
					   this.#ball.isLeft( this.#paddle ) 
					|| this.#ball.isRight( this.#paddle ) 
				)
			) 
		) { 
			return; 
		}
		const maxX = this.#canvas.width - this.#paddle.width;
		const canvasClientX = this.#canvas.getBoundingClientRect().x;
		let x = mouseMoveEvent.clientX - canvasClientX - this.#paddle.width / 2;
		if ( x < 0 ) { x = 0; }
		if ( x > maxX ) { x = maxX; }
		this.#paddle.x = x;
	}

	/**
	 * Handle the keyboard events.
	 * @memberof module:paddleandballgame~PaddleAndBallGame#
   * @access private
	 * @function onWindowKeyboardEvent
	 * @param {Event} keyboardEvent 
	 */
	#onWindowKeyboardEvent( keyboardEvent ) {

		// Press START to start/pause
		if (
			   ( keyboardEvent.type === "keyup" ) 
			&& ( keyboardEvent.key === this.#keyBindings.START )
		) {
			if ( this.#isGameOver ) { return this.#restart(); }
			this.#isPaused = !this.#isPaused;
			return;
		}

		// Paddle does not move when game is paused, or non-control keys are being
		// pressed.
		if ( 
			   this.#isPaused 
			|| (
				   ( keyboardEvent.key !== this.#keyBindings.LEFT )
				&& ( keyboardEvent.key !== this.#keyBindings.RIGHT )
			)
		) { 
			return; 
		}

		// Check out if the pressed key is already registered
		const action = ( keyboardEvent.type == "keyup" ) ? "delete" : "add";
		const hasKey = this.#pressedKeys.has( keyboardEvent.key );
		if ( 
				 ( ( action == "add" ) && hasKey )
			|| ( ( action == "delete" ) && !hasKey )
		) {
			return;
		}
		// Register key press
		this.#pressedKeys[ action ]( keyboardEvent.key );
		this.#activeKey = null;
		for( this.#activeKey of this.#pressedKeys );

	}

	/** 
	 * Converts a touchend event to a keyboard event.
	 * @memberof module:paddleandballgame~PaddleAndBallGame#
   * @access private
	 * @function onWindowTouchEvent
	 * @param {TouchEvent} touchEvent 
	 */
	#onWindowTouchEvent( touchEvent ) {

		this.#isUsingTouch = true;

		// Restart game
		if ( this.#isGameOver ) { return this.#restart(); }

		const isStart = ( touchEvent.type === "touchstart" );

		// Unpause
		if ( this.#isPaused && !isStart ) {
			this.#isPaused = false;
			return;
		}

		const keyEventType = "key" + ( isStart ? "down" : "up" );

		// Determine which side of the screen the touch affected
		const clientX = touchEvent.changedTouches[ 0 ].clientX;
		const hasTouchedLeft = ( clientX < window.innerWidth / 2 );

		// Convert tactile input to equivalent key press
		const touchedKey = this.#keyBindings[ hasTouchedLeft ? "LEFT" : "RIGHT" ];
		this.#onWindowKeyboardEvent( new KeyboardEvent( 
			keyEventType, { key: touchedKey }
		) );

	}	

	/**
	 * Pause the game if the document is hidden.
	 * @param {Event} visibilityChangeEvent 
	 */
	#onDocumentVisibilityChange( visibilityChangeEvent ) {
		if ( this.#isPaused ) { return; }
		if ( document.visibilityState === "hidden" ) { this.#isPaused = true; }
	}

	/**
	 * Pause the game if focus is away from the `window`.
	 * @param {Event} blurEvent 
	 */
	#onWindowBlur( blurEvent ) { this.#isPaused = true; }

	/** 
	 * Update the state of the game objects.
	 * @memberof module:paddleandballgame~PaddleAndBallGame#
   * @access private
	 * @function updateGameState
	 */
	#updateGameState() {

		if ( this.#isPaused ) { return; }

		// Move ball
		this.#ball.x += this.#ballVelocity.x;
		this.#ball.y += this.#ballVelocity.y;

		// Check and account for ball vs. wall collision.
		this.#isGameOver = this.#processBallVsWallCollision();
		if ( this.#isGameOver ) { 
			this.#isPaused = true; 
			this.#activeKey = null;
			this.#pressedKeys = new Set();
			return;
		}

		// Move paddle. It can move horizontally as fast as the ball moves.
		if ( this.#activeKey ) {
			const moveLeft = ( this.#activeKey === this.#keyBindings.LEFT );
			this.#paddle.x += this.#ballVelocityMagnitude * ( moveLeft ? -1 : 1 );
			if ( this.#paddle.minX < 0 ) { this.#paddle.minX = 0; }
			if ( this.#paddle.maxX > this.#canvas.width ) { 
				this.#paddle.maxX = this.#canvas.width;
			}
		}

		// Check and account for ball vs. paddle collision.
		this.#processBallVsPaddleCollision();

		// Check and account for ball vs. bricks collision. 
		this.#isGameOver = this.#processBallVsBricksCollision();
		if ( this.#isGameOver ) { 
			this.#isPaused = true;
			this.#activeKey = null;
			this.#pressedKeys = new Set();
		}

	}

	/**
	 * Draw a text in a rectangle.
	 * @memberof module:paddleandballgame~PaddleAndBallGame#
   * @access private
	 * @function drawTextBox
	 * @param {String[]} lines
	 * @param {Number}   centerX
	 * @param {Number}   centerY
	 */
	#drawTextBox( 
		lines, 
		centerX = this.#canvas.width / 2, 
		centerY = this.#canvas.height / 2 
	) {

		const horizontalPadding = 20;
		const verticalPadding = 10;

		const numberOfLines = lines.length;
		const fontSize = Math.floor( this.#canvas.width / 25 );

		let maxLineWidth = 0;
		let maxLineHeight = 0;

		this.#context.save();

		this.#context.font = fontSize + "px monospace";
		this.#context.textAlign = "center";
		this.#context.textBaseline = "middle";

		for( let l = numberOfLines - 1; l >= 0; l = l - 1 ) {
			const textMetrics = this.#context.measureText( lines[ l ] );
			const lineWidth = 0 
				+ textMetrics.actualBoundingBoxLeft 
				+ textMetrics.actualBoundingBoxRight
			;
			const lineHeight = 2 * ( 0
				+ textMetrics.actualBoundingBoxAscent 
				+ textMetrics.actualBoundingBoxDescent
			);
			maxLineWidth = ( lineWidth > maxLineWidth ) ? lineWidth : maxLineWidth;
			maxLineHeight = ( lineHeight > maxLineHeight ) 
				? lineHeight 
				: maxLineHeight
			;
		}

		const textHeight = maxLineHeight * numberOfLines;

		this.#context.lineWidth = 1;
		const borderWidth = 2 * this.#context.lineWidth;
		const paddingWidth = 2 * horizontalPadding;
		const paddingHeight = 2 * verticalPadding;
		const topLeftX = centerX - maxLineWidth / 2 - horizontalPadding;
		const topLeftY = centerY - textHeight / 2 - verticalPadding;
		const boxWidth = maxLineWidth + paddingWidth;
		const boxHeight = textHeight + paddingHeight;

		// Pop-up's border
		this.#context.strokeStyle = "#fff";
		this.#context.lineWidth = 2;
		this.#context.strokeRect( 
			topLeftX - this.#context.lineWidth, 
			topLeftY - this.#context.lineWidth, 
			boxWidth + borderWidth, 
			boxHeight + borderWidth 
		);
		// Pop-up's background
		this.#context.fillStyle = "rgba(64, 64, 64, 0.666)";
		this.#context.fillRect( topLeftX, topLeftY, boxWidth, boxHeight );
		// Pop-up's text
		this.#context.fillStyle = "#fff";
		for( let l = numberOfLines - 1; l >= 0; l = l - 1 ) {
			const lineY = topLeftY + paddingHeight + l * maxLineHeight;
			this.#context.fillText( lines[ l ], centerX, lineY );
		}

		this.#context.restore();

	}

	/** 
	 * Draw an overlay with information relevant to the game's state.
	 * @memberof module:paddleandballgame~PaddleAndBallGame#
	 * @access private
	 * @function paintPauseOverlay
	 */
	#paintPauseOverlay() {
		if ( !this.#isPaused ) { return; }
		const message = [];
		if ( !this.#isGameOver ) {
			const remaining = this.#bricks.length + " out of " + this.#numberOfBricks;
			const blockName = "brick" + ( ( this.#bricks.length == 1 ) ? "" : "s" );
			message.push( remaining + " " + blockName + " remaining." );
			if ( this.#isUsingTouch ) {
				// This scenario is not possible. Touch controls do not pause the game.
				message.push( "Touch anywhere to start." );
				message.push( "Touch left/right side of the screen" );
				message.push( "to move paddle to the left/right." );
			} else {	
				message.push( "Click to start/pause game." );
				message.push( "Move mouse to move paddle." );
			}
			this.#drawTextBox( message );
			return;
		}
		if ( this.#bricks.length !== 0 ) {
			message.push( "G A M E   O V E R" );
			const destroyed = this.#numberOfBricks - this.#bricks.length;
			const blockName = "brick" + ( ( destroyed == 1 ) ? "" : "s" );
			message.push( "You destroyed " + destroyed + " " + blockName + "." );
		} else {
			message.push( "Y O U   W O N !!!" );
		}
		const action = this.#isUsingTouch ? "Touch" : "Click";
		message.push( action + " anywhere to start over." );
		this.#drawTextBox( message );
	}	

	/** 
	 * Draw all elements on the canvas.
	 * @memberof module:paddleandballgame~PaddleAndBallGame#
   * @access private
	 * @function paintFrame
	 */
	#paintFrame() {

		this.#context.fillStyle = this.#field.colour;
		this.#context.fillRect( ...this.#field.rect );

		this.#context.fillStyle = this.#paddle.colour;
		this.#context.fillRect( ...this.#paddle.rect );

		this.#context.fillStyle = this.#ball.colour;
		this.#context.fillRect( ...this.#ball.rect );

		this.#context.strokeStyle = this.#field.colour;
		for( let b = this.#bricks.length - 1; b >= 0; b = b - 1 ) {
			const block = this.#bricks[ b ];
			this.#context.fillStyle = block.colour;
			this.#context.fillRect( ...block.rect );
			this.#context.beginPath();
			this.#context.rect( ...block.rect );
			this.#context.stroke();
		}

		this.#paintPauseOverlay();

	}

	/**
	 * Handle the main animation loop.
	 * @memberof module:paddleandballgame~PaddleAndBallGame#
	 * @access private
	 * @function handleAnimationFrame
	 * @param {Number} timestamp 
	 */
	#handleAnimationFrame( timestamp ) {
		const handler = this.#handleAnimationFrame.bind( this );
		this.#requestAnimationFrameId = window.requestAnimationFrame( handler );
		if ( timestamp - this.#previousFrameTimestamp < this.#minFrameDuration ) {
			return;
		}
		this.#previousFrameTimestamp = timestamp;
		this.#updateGameState();
		this.#paintFrame();
	}

	/**
	 * Bind events to their appropriate handlers. 
	 * @memberof module:paddleandballgame~PaddleAndBallGame#
	 * @access private
	 * @function addEventListeners
	 */
	#addEventListeners() {
		this.#handleWindowMouseUp = this.#onWindowMouseUp.bind( this );
		window.addEventListener( "mouseup", this.#handleWindowMouseUp );
		this.#handleWindowMouseMove = this.#onWindowMouseMove.bind( this );
		window.addEventListener( "mousemove", this.#handleWindowMouseMove );
		this.#handleWindowKeyboardEvent = this.#onWindowKeyboardEvent.bind( this );
		window.addEventListener( "keydown", this.#handleWindowKeyboardEvent );
		window.addEventListener( "keyup", this.#handleWindowKeyboardEvent );
		this.#handleWindowTouchEvent = this.#onWindowTouchEvent.bind( this );
		window.addEventListener( "touchstart", this.#handleWindowTouchEvent );
		window.addEventListener( "touchend", this.#handleWindowTouchEvent );
		this.#handleWindowBlur = this.#onWindowBlur.bind( this );
		window.addEventListener( "blur", this.#handleWindowBlur );
		this.#handleDocumentVisibilityChange = this
			.#onDocumentVisibilityChange
			.bind( this )
		;
		document.addEventListener( 
			"visibilitychange", this.#handleDocumentVisibilityChange
		);
	}	

	/** 
	 * Flags when the game is paused.
	 * @type {Boolean} 
	 */
	get isPaused() { return this.#isPaused; };
	set isPaused( value ) { this.#isPaused = !!value; }

	/** A method to free up game's resources. */
	dispose() {
		window.cancelAnimationFrame( this.#requestAnimationFrameId );
		document.removeEventListener(
			"visibilitychange", this.#handleDocumentVisibilityChange
		);
		window.removeEventListener( "blur", this.#handleWindowBlur );
		window.removeEventListener( "touchend", this.#handleWindowTouchEvent );
		window.removeEventListener( "touchstart", this.#handleWindowTouchEvent );
		window.removeEventListener( "mousemove", this.#handleWindowMouseMove );
		window.removeEventListener( "mouseup", this.#handleWindowMouseUp );
		window.addEventListener( "keydown", this.#handleWindowKeyboardEvent );
		window.addEventListener( "keyup", this.#handleWindowKeyboardEvent );
	}

	/**
	 * Create a new paddle and ball game.
	 * @param {HTMLCanvasElement} canvas The `canvas` where the game will be rendered.
	 */
	constructor( canvas ) {
		
		if ( !( canvas instanceof HTMLCanvasElement ) ) { 
			console.error( "Game requires a canvas element" );
			return; 
		}
		
		this.#setUpCanvasAndContext( canvas );

		this.#createElements();

		this.#addEventListeners();

		window.requestAnimationFrame( this.#handleAnimationFrame.bind( this ) );

	}

} 

export { PaddleAndBallGame }